<script setup lang="ts">
const t2 = useT2({})
const mobile = useMobile()

const props = defineProps<{
  bulk: NN<gen.ProductListingBoxUiPriceFragment>['bulk']
  disabled: boolean
  max: number
  min: number
  start: number
  inStock: boolean
  stock: number | None
  priceUnit: number
  value: number
  out: boolean
  valueSet(value: number): void
  total: string | number | None
  size: 's' | 'm' | 'l'
  settingsQty: gen.ProductSettingsQtyFragment | None
}>()

const elem = useTemplateRef('elem')
const isOpen = ref(false)
const dropdownRender = ref(false)
const blockBlur = shallowRef(false)
const deleteOpened = ref(false)

const uiDisabled = computed(() => {
  const { max, min } = props
  return !max || max < min
})

const qty = computed(() => {
  const { inStock, stock, min, value } = props
  return {
    remove: !inStock || value <= min,
    reduce: inStock && stock && value > stock && stock >= min ? stock : undefined,
  }
})

function countDecimalPlaces(number: number): number {
  const [base = '', exponent = '0'] = String(number).split('e')
  const index = base.indexOf('.')
  const de = index >= 0 ? base.length - index - 1 : 0
  return Math.max(de - Number.parseInt(exponent, 10), 0)
}

function amount(count: -1 | 1) {
  const uiV = qty.value
  if (count < 0 && uiV.reduce) {
    props.valueSet(uiV.reduce)
  } else if (count < 0 && uiV.remove) {
    deleteOpened.value = true
  } else {
    const step = helpers.toPositive(props.settingsQty?.step, 1)
    const mx = count > 0 ? 1 : -1
    props.valueSet(helpers.toPositive(helpers.add({ decimal: countDecimalPlaces(step) }, props.value, step * mx), 0))
  }
}

const offRender = watch(
  () => isOpen.value && !uiDisabled.value,
  (on) => {
    if (!on) return
    dropdownRender.value = true
    nextTick(() => offRender())
  }
)

const time = useTimeout()
function onBlur(num: number) {
  if (blockBlur.value) return
  props.valueSet(num)
  isOpen.value = false
}
function select(num: number) {
  blockBlur.value = true
  time.set(() => (blockBlur.value = false), 100)
  props.valueSet(num)
  isOpen.value = false
}
</script>

<template>
  <div ref="elem" class="_relative _row _w100p _pl02 _pr02" :class="size === 'l' ? $style.big : size === 'm' ? $style.medium : $style.small">
    <ButtonPlus type="minus" :disabled :mark="!!qty.reduce" :class="[qty.reduce && $style.reduce, !qty.reduce && 'IconTrash']" :size @click.passive="amount(-1)">
      <template v-if="qty.reduce">{{ qty.reduce }}</template>
      <IconTrash v-else-if="qty.remove" />
    </ButtonPlus>

    <div :class="[$style.value, out && '_red']">
      <UiNumberFalling v-memo="[value]" :value />

      <template v-if="size !== 's' && total">
        <UiPriceCounter v-if="typeof total === 'number'" :price="total" class="_fs09" />
        <b v-else-if="total" class="_mono _fs09">{{ total }}</b>
      </template>

      <ProductBtnBuyInputInput :disabled="!inStock || disabled" :settings-qty :value :stock :value-set="(i) => onBlur(i)" :class="$style.inp" class="_absolute _inset" @focus.passive="isOpen = true" />
    </div>

    <ButtonPlus type="plus" :size :disabled mark @click.passive="amount(1)" />

    <UiPreview4 v-model="deleteOpened" :elem="() => elem" :dropdown="mobile">
      <div class="_row m_ctrX m_pr1 m_pl1">
        <ButtonAction :width="mobile ? '100p' : '200'" :txt="t2('Remove')" color="red" class="IconTrash" :submit="() => unvoid(valueSet(0))">
          <IconTrash class="_svg16" />
        </ButtonAction>
      </div>
    </UiPreview4>

    <LazyProductBtnBuyQtyDropdown v-if="dropdownRender" v-model:open="isOpen" v-model:blur="blockBlur" :start :max :min :value :select :elem="() => elem" :bulk :price-unit />
  </div>
</template>

<style module>
.big {
  composes: global(_gap08);
  --size: 36px;
}

.medium {
  --size: 34px;
  composes: global(_gap02);
}

.small {
  --size: 34px;
  composes: global(_gap02);
}

.value {
  composes: global(_relative);
  composes: global(_flex);
  composes: global(_b0);

  border-width: 0 1px;
  height: auto;

  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_ctrX);
  composes: global(_wrap);

  composes: global(_pt04);
  composes: global(_pb04);
  composes: global(_pr02);
  composes: global(_pl02);

  gap: 0px 10px;
}

.value:hover {
  border-color: var(--black-5);
}

.inp {
  composes: global(_opacity0);
  transition: 80ms opacity var(--tran2);
  border: 1px solid transparent;
  border-width: 0 1px;

  left: -1px;
  right: -1px;
  width: calc(100% + 2px);
}

.inp:focus {
  opacity: 1;
  border-color: var(--black-5);
}
</style>
